import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import { Activity, AlertTriangle, ShoppingCart, Sparkles } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import {
  activationStatus,
  eventLabel,
  frictionLevel,
  purchaseStage,
  type ContactSignalsResponse,
} from "./contactSignals";

function Block({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-2 text-fabrik-text-3">
          <span className="[&>svg]:size-3.5">{icon}</span>
          <p className="text-[11px] uppercase tracking-wider font-semibold">
            {title}
          </p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export function ContactProductSignals({
  signals,
  loading = false,
}: {
  signals: ContactSignalsResponse | null;
  loading?: boolean;
}) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!signals || !signals.configured) return null;

  const stage = purchaseStage(signals.funnel);
  const activation = activationStatus(signals.activation);
  const friction = frictionLevel(signals.friction);

  const frictionTone =
    friction.key === "high"
      ? "bg-fabrik-red-soft text-fabrik-red"
      : friction.key === "some"
        ? "bg-fabrik-orange-soft text-fabrik-orange"
        : "bg-muted text-muted-foreground";

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Block icon={<ShoppingCart />} title="Intention d'achat">
          <p className="text-[15px] font-semibold text-foreground">
            {stage.label}
          </p>
          <p className="text-[11.5px] text-fabrik-text-3 mt-0.5 tabular-nums">
            {signals.funnel.paywall_shown} paywall ·{" "}
            {signals.funnel.upgrade_clicked} upgrade ·{" "}
            {signals.funnel.checkout_success} conv.
          </p>
        </Block>
        <Block icon={<Sparkles />} title="Activation">
          <p className="text-[15px] font-semibold text-foreground">
            {activation.label}
          </p>
          <p className="text-[11.5px] text-fabrik-text-3 mt-0.5 tabular-nums">
            {signals.activation.plan_created} plan(s) · profil{" "}
            {signals.activation.profile_completed > 0 ? "✓" : "—"}
          </p>
        </Block>
        <Block icon={<AlertTriangle />} title="Friction">
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium",
              frictionTone,
            )}
          >
            {friction.label}
          </span>
          {friction.key !== "none" && (
            <p className="text-[11.5px] text-fabrik-text-3 mt-1 tabular-nums">
              {signals.friction.exception} err · {signals.friction.rageclick}{" "}
              rage · {signals.friction.login_failed} login échoué(s)
            </p>
          )}
        </Block>
      </div>

      <Block icon={<Activity />} title="Activité récente">
        {signals.timeline.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground">
            Aucun événement récent.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border/40">
            {signals.timeline.map((e, i) => (
              <li
                key={`${e.event}-${e.timestamp}-${i}`}
                className="flex items-center justify-between py-1.5"
              >
                <span className="text-[13px] text-foreground">
                  {eventLabel(e.event)}
                </span>
                <span className="text-[11.5px] text-fabrik-text-3">
                  {formatDistanceToNowStrict(new Date(e.timestamp), {
                    locale: fr,
                    addSuffix: true,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Block>
    </div>
  );
}
